import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  Group,
  Image,
  Loader,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminShell } from '../../components/admin-shell/AdminShell';
import { AdminTableSection } from '../../components/admin-shell/AdminTableSection';
import { showToast } from '../../lib/toast';
import { adminDelete, adminGet, adminPatch, adminPost } from './_api';
import { usePaginatedQuery } from './_hooks';

type FeedbackStatus = 'new' | 'reviewed' | 'archived';

interface Screenshot {
  attachment_id: string;
  url: string;
  filename: string;
  mime: string;
  width_px: number;
  height_px: number;
}

interface Submitter {
  id: string;
  email: string | null;
  display_name: string | null;
}

interface FeedbackRow {
  id: string;
  category: string;
  message: string;
  status: FeedbackStatus;
  page_url: string | null;
  user_agent: string | null;
  active_hat: string | null;
  submitter: Submitter | null;
  anonymous_email: string | null;
  screenshots: Screenshot[];
  created_at: string;
}

interface FeedbackComment {
  id: string;
  body: string;
  created_at: string;
  author: Submitter;
}

interface FeedbackDetail extends FeedbackRow {
  updated_at: string;
  comments: FeedbackComment[];
}

export const Route = createFileRoute('/admin/feedback')({
  component: AdminFeedbackPage,
});

const CATEGORY_COLORS: Record<string, string> = {
  bug: 'red',
  idea: 'tide',
  copy: 'grape',
  design: 'violet',
  praise: 'green',
  other: 'gray',
};

function AdminFeedbackPage() {
  const [status, setStatus] = useState<string | null>('new');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error, page, setPage, totalPages, bindFilter } = usePaginatedQuery<FeedbackRow>({
    queryKey: ['admin', 'feedback'],
    endpoint: '/api/admin/feedback',
    filters: { status },
  });

  async function setRowStatus(id: string, next: FeedbackStatus): Promise<void> {
    try {
      await adminPatch(`/api/admin/feedback/${id}`, { status: next });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] });
      showToast({ title: 'Updated', body: `Marked as ${next}.`, kind: 'success' });
    } catch {
      showToast({ title: 'Could not update', body: 'Please try again.', kind: 'error' });
    }
  }

  return (
    <AdminShell>
      <Stack gap="md">
        <Title order={2}>Feedback</Title>
        <Text c="dimmed" size="sm">
          Submissions from the in-app feedback button. Click a row to open the full detail and triage thread.
        </Text>

        <Group gap="sm" wrap="wrap">
          <Select
            placeholder="Status"
            data={[
              { value: 'new', label: 'New' },
              { value: 'reviewed', label: 'Reviewed' },
              { value: 'archived', label: 'Archived' },
            ]}
            clearable
            value={status}
            onChange={bindFilter(setStatus)}
            style={{ minWidth: 160 }}
          />
        </Group>

        <AdminTableSection<FeedbackRow>
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          emptyMessage="No feedback in this view yet."
          header={
            <Table.Tr>
              <Table.Th>When</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th>Message</Table.Th>
              <Table.Th>From</Table.Th>
              <Table.Th>Page</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th> </Table.Th>
            </Table.Tr>
          }
          renderRow={(f) => (
            <Table.Tr key={f.id} style={{ cursor: 'pointer' }} onClick={() => setSelectedId(f.id)}>
              <Table.Td>{new Date(f.created_at).toLocaleString()}</Table.Td>
              <Table.Td>
                <Badge color={CATEGORY_COLORS[f.category] ?? 'gray'} variant="light">
                  {f.category}
                </Badge>
              </Table.Td>
              <Table.Td style={{ maxWidth: 360 }}>
                <Text size="sm" lineClamp={2}>
                  {f.message}
                </Text>
                {f.screenshots.length > 0 ? (
                  <Text size="xs" c="dimmed" mt={4}>
                    📎 {f.screenshots.length} screenshot{f.screenshots.length > 1 ? 's' : ''}
                  </Text>
                ) : null}
              </Table.Td>
              <Table.Td>
                <Text size="sm">{submitterLabel(f)}</Text>
                {f.active_hat ? (
                  <Text size="xs" c="dimmed">
                    {f.active_hat}
                  </Text>
                ) : null}
              </Table.Td>
              <Table.Td style={{ maxWidth: 200 }}>
                {f.page_url ? (
                  <Tooltip label={f.page_url} multiline w={320}>
                    <Text size="xs" truncate>
                      {pagePath(f.page_url)}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text size="xs" c="dimmed">
                    –
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Badge color={statusColor(f.status)} variant="light">
                  {f.status}
                </Badge>
              </Table.Td>
              <Table.Td onClick={(e) => e.stopPropagation()}>
                <Group gap="xs" wrap="nowrap">
                  {f.status !== 'reviewed' ? (
                    <ActionIcon variant="subtle" color="tide" aria-label="Mark reviewed" onClick={() => void setRowStatus(f.id, 'reviewed')}>
                      ✓
                    </ActionIcon>
                  ) : null}
                  {f.status !== 'archived' ? (
                    <ActionIcon variant="subtle" color="gray" aria-label="Archive" onClick={() => void setRowStatus(f.id, 'archived')}>
                      ⌫
                    </ActionIcon>
                  ) : null}
                  {f.status !== 'new' ? (
                    <ActionIcon variant="subtle" color="blue" aria-label="Mark new" onClick={() => void setRowStatus(f.id, 'new')}>
                      ↺
                    </ActionIcon>
                  ) : null}
                </Group>
              </Table.Td>
            </Table.Tr>
          )}
        />
      </Stack>

      <Drawer
        opened={selectedId !== null}
        onClose={() => setSelectedId(null)}
        position="right"
        size="lg"
        padding="lg"
        title="Feedback detail"
      >
        {selectedId !== null ? <FeedbackDetailPanel feedbackId={selectedId} /> : null}
      </Drawer>
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------
// Detail panel — full submission, screenshots, status controls, comment thread.
// ---------------------------------------------------------------------------

function FeedbackDetailPanel({ feedbackId }: { feedbackId: string }) {
  const queryClient = useQueryClient();
  const [commentBody, setCommentBody] = useState('');
  const [busy, setBusy] = useState(false);

  const detailKey = ['admin', 'feedback', 'detail', feedbackId] as const;
  const { data: detail, isLoading } = useQuery({
    queryKey: detailKey,
    queryFn: () => adminGet<FeedbackDetail>(`/api/admin/feedback/${feedbackId}`),
  });

  async function refresh(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: detailKey }),
      queryClient.invalidateQueries({ queryKey: ['admin', 'feedback'] }),
    ]);
  }

  async function changeStatus(next: FeedbackStatus): Promise<void> {
    setBusy(true);
    try {
      await adminPatch(`/api/admin/feedback/${feedbackId}`, { status: next });
      await refresh();
      showToast({ title: 'Updated', body: `Marked as ${next}.`, kind: 'success' });
    } catch {
      showToast({ title: 'Could not update', body: 'Please try again.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function addComment(): Promise<void> {
    const body = commentBody.trim();
    if (body.length === 0) return;
    setBusy(true);
    try {
      await adminPost(`/api/admin/feedback/${feedbackId}/comments`, { body });
      setCommentBody('');
      await refresh();
    } catch {
      showToast({ title: 'Could not add comment', body: 'Please try again.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  async function deleteComment(commentId: string): Promise<void> {
    setBusy(true);
    try {
      await adminDelete(`/api/admin/feedback/${feedbackId}/comments/${commentId}`);
      await refresh();
    } catch {
      showToast({ title: 'Could not delete comment', body: 'Please try again.', kind: 'error' });
    } finally {
      setBusy(false);
    }
  }

  if (isLoading || detail === undefined) {
    return (
      <Group py="lg">
        <Loader size="sm" />
        <Text c="dimmed">Loading…</Text>
      </Group>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Group gap="xs">
          <Badge color={CATEGORY_COLORS[detail.category] ?? 'gray'} variant="light">
            {detail.category}
          </Badge>
          <Badge color={statusColor(detail.status)} variant="light">
            {detail.status}
          </Badge>
        </Group>
        <Text size="xs" c="dimmed">
          {new Date(detail.created_at).toLocaleString()}
        </Text>
      </Group>

      <Paper withBorder p="sm" radius="md">
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          {detail.message}
        </Text>
      </Paper>

      {detail.screenshots.length > 0 ? (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Screenshots
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {detail.screenshots.map((s) => (
              <a key={s.attachment_id} href={s.url} target="_blank" rel="noreferrer" title={s.filename}>
                <Image
                  src={s.url}
                  alt={s.filename}
                  radius="sm"
                  fit="cover"
                  h={120}
                  style={{ border: '1px solid var(--mantine-color-ink-1)' }}
                />
              </a>
            ))}
          </SimpleGrid>
        </Stack>
      ) : null}

      <Stack gap={4}>
        <DetailRow label="From" value={submitterLabel(detail)} />
        {detail.active_hat ? <DetailRow label="Hat" value={detail.active_hat} /> : null}
        {detail.page_url ? (
          <Group gap="xs" wrap="nowrap" align="baseline">
            <Text size="xs" c="dimmed" w={80} style={{ flexShrink: 0 }}>
              Page
            </Text>
            <Anchor href={detail.page_url} target="_blank" rel="noreferrer" size="sm" lineClamp={1}>
              {pagePath(detail.page_url)}
            </Anchor>
          </Group>
        ) : null}
        {detail.user_agent ? <DetailRow label="Device" value={detail.user_agent} dimmed /> : null}
      </Stack>

      <Group gap="xs">
        <Text size="xs" c="dimmed">
          Set status:
        </Text>
        {(['new', 'reviewed', 'archived'] as const).map((s) => (
          <Button
            key={s}
            size="xs"
            variant={detail.status === s ? 'filled' : 'light'}
            color={statusColor(s)}
            disabled={busy || detail.status === s}
            onClick={() => void changeStatus(s)}
          >
            {s}
          </Button>
        ))}
      </Group>

      <Divider label="Internal comments" labelPosition="left" />

      <Stack gap="sm">
        {detail.comments.length === 0 ? (
          <Text size="sm" c="dimmed">
            No comments yet. Leave a note for the team.
          </Text>
        ) : (
          detail.comments.map((cm) => (
            <Paper key={cm.id} withBorder p="sm" radius="md">
              <Group justify="space-between" align="flex-start" mb={4} wrap="nowrap">
                <Text size="xs" fw={600}>
                  {personLabel(cm.author)}
                </Text>
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" c="dimmed">
                    {new Date(cm.created_at).toLocaleString()}
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="xs"
                    aria-label="Delete comment"
                    disabled={busy}
                    onClick={() => void deleteComment(cm.id)}
                  >
                    ×
                  </ActionIcon>
                </Group>
              </Group>
              <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                {cm.body}
              </Text>
            </Paper>
          ))
        )}
      </Stack>

      <Box>
        <Textarea
          placeholder="Add an internal note…"
          autosize
          minRows={2}
          maxRows={8}
          maxLength={4000}
          value={commentBody}
          onChange={(e) => setCommentBody(e.currentTarget.value)}
          disabled={busy}
        />
        <Group justify="flex-end" mt="xs">
          <Button size="xs" color="tide" loading={busy} disabled={commentBody.trim().length === 0} onClick={() => void addComment()}>
            Add comment
          </Button>
        </Group>
      </Box>
    </Stack>
  );
}

function DetailRow({ label, value, dimmed }: { label: string; value: string; dimmed?: boolean }) {
  return (
    <Group gap="xs" wrap="nowrap" align="baseline">
      <Text size="xs" c="dimmed" w={80} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" {...(dimmed ? { c: 'dimmed' as const } : {})} style={{ wordBreak: 'break-word' }}>
        {value}
      </Text>
    </Group>
  );
}

function submitterLabel(f: { submitter: Submitter | null; anonymous_email: string | null }): string {
  if (f.submitter) return personLabel(f.submitter);
  if (f.anonymous_email) return f.anonymous_email;
  return 'Anonymous';
}

function personLabel(p: Submitter): string {
  return p.display_name ?? p.email ?? p.id.slice(0, 8);
}

function statusColor(status: string): string {
  if (status === 'new') return 'blue';
  if (status === 'reviewed') return 'tide';
  return 'gray';
}

function pagePath(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search;
  } catch {
    return url;
  }
}
